import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Work as WorkIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { cadastrarVaga } from '../../services/vaga';
import { obterIdEmpresa } from '../../utils/empresa';
import { normalizarTexto, normalizarDescricao } from '../../utils/normalization';

interface Vaga {
  nome: string;
  cargo: string;
  salario: number | '';
  descricao: string;
  ativa: boolean;
  empresa: {
    id: number;
  };
}

interface VagaForm {
  nome: string;
  cargo: string;
  salario: string; // Salário formatado como string
  salarioNumerico: number | '';
  descricao: string;
  ativa: boolean;
  id_empresa: number;
}

const CadastrarVaga: React.FC = () => {
  const navigate = useNavigate();
  const [vaga, setVaga] = useState<VagaForm>({
    nome: '',
    cargo: '',
    salario: '',
    salarioNumerico: '',
    descricao: '',
    ativa: true,
    id_empresa: parseInt(obterIdEmpresa() || '0')
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | ''; text: string }>({ 
    type: '', 
    text: '' 
  });

  // Função para formatar valor em moeda brasileira
  const formatarMoeda = (valor: string): string => {
    // Remove tudo que não é dígito
    const numeros = valor.replace(/\D/g, '');
    
    if (!numeros) return '';
    
    // Converte para número e divide por 100 para ter centavos
    const valorNumerico = parseFloat(numeros) / 100;
    
    // Formata como moeda brasileira
    return valorNumerico.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para extrair valor numérico da string formatada
  const extrairValorNumerico = (valorFormatado: string): number => {
    if (!valorFormatado) return 0;
    
    // Remove formatação e converte para número
    const numeros = valorFormatado.replace(/\D/g, '');
    return parseFloat(numeros) / 100;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setVaga(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'salario') {
      const valorFormatado = formatarMoeda(value);
      const valorNumerico = extrairValorNumerico(valorFormatado);
      
      setVaga(prev => ({
        ...prev,
        salario: valorFormatado,
        salarioNumerico: valorNumerico || ''
      }));
    } else {
      setVaga(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica
    if (!vaga.nome || !vaga.cargo || !vaga.descricao) {
      setMessage({ type: 'error', text: 'Por favor, preencha todos os campos obrigatórios.' });
      return;
    }

    // Validação da empresa
    if (!vaga.id_empresa || vaga.id_empresa === 0) {
      setMessage({ type: 'error', text: 'Erro: ID da empresa não encontrado. Faça login novamente.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const dadosVaga = {
        nomeVaga: normalizarTexto(vaga.nome),
        cargo: normalizarTexto(vaga.cargo),
        salario: vaga.salarioNumerico || 0,
        descricaoVaga: normalizarDescricao(vaga.descricao),
        ativo: vaga.ativa,
        dataCriacao: new Date().toISOString().split('T')[0], // Data atual no formato YYYY-MM-DD
        idEmpresa: vaga.id_empresa
      };
      
      const response = await cadastrarVaga(dadosVaga);

      setMessage({ type: 'success', text: 'Vaga cadastrada com sucesso!' });
      
      // Limpar formulário
      setVaga({
        nome: '',
        cargo: '',
        salario: '',
        salarioNumerico: '',
        descricao: '',
        ativa: true,
        id_empresa: parseInt(obterIdEmpresa() || '0')
      });

      // Permanecer na página de cadastro após sucesso

    } catch (error: any) {
      console.error('Erro ao cadastrar vaga:', error);
      
      let errorMessage = 'Erro ao cadastrar vaga. Tente novamente.';
      
      if (error.response?.status === 500) {
        if (error.response?.data?.message) {
          errorMessage = `Erro interno do servidor: ${error.response.data.message}`;
        } else if (error.response?.data?.error) {
          errorMessage = `Erro interno: ${error.response.data.error}`;
        } else {
          errorMessage = 'Erro interno do servidor. Verifique os dados e tente novamente.';
        }
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Dados inválidos. Verifique os campos preenchidos.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <WorkIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Cadastrar Nova Vaga
          </Typography>
        </Box>

        {message.text && (
          <Alert severity={message.type as any} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome da Vaga"
              name="nome"
              value={vaga.nome}
              onChange={handleChange}
              fullWidth
              required
              placeholder="Ex: Desenvolvedor Full Stack"
            />

            <TextField
              label="Cargo"
              name="cargo"
              value={vaga.cargo}
              onChange={handleChange}
              fullWidth
              required
              placeholder="Ex: Analista de Sistemas"
            />

            <TextField
              label="Salário"
              name="salario"
              value={vaga.salario}
              onChange={handleChange}
              fullWidth
              type="text"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />

            <TextField
              label="Descrição"
              name="descricao"
              value={vaga.descricao}
              onChange={handleChange}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Descreva as responsabilidades, requisitos e benefícios da vaga..."
            />


            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Cadastrar Vaga'}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/dashboard-empresa')}
                disabled={loading}
                fullWidth
              >
                Voltar
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CadastrarVaga;